import type { Sku, StockItem } from '@commercelayer/sdk'

/**
 * Checks if a SKU (Stock Keeping Unit) is in stock.
 *
 * Determines if any of the stock items for the SKU are available.
 *
 * @param {Sku} sku - The SKU to check for stock availability.
 * @returns {boolean} True if the SKU has at least one stock item in stock, false otherwise.
 *
 * @example
 * // Returns true if the SKU has available stock
 * isInStock(skuWithAvailableStock)
 *
 * @example
 * // Returns false if the SKU has no stock items
 * isInStock(skuWithNoStockItems)
 */
export const isInStock = (sku: Sku) =>
  sku.stock_items?.some(isStockItemInStock) ?? false

/**
 * Determines if a SKU has exactly one last item in stock.
 *
 * Checks if:
 * 1. The SKU has stock items
 * 2. Exactly one stock item has a single item left
 * 3. All other stock items are out of stock
 *
 * @param {Sku} sku - The SKU to check for the last item in stock.
 * @returns {boolean} True if the SKU meets the last item in stock criteria, false otherwise.
 *
 * @example
 * // Returns true if one stock item has the last item
 * isLastInStockFn(skuWithLastItem)
 *
 * @example
 * // Returns false if multiple items are in stock
 * isLastInStockFn(skuWithMultipleAvailableItems)
 */
export const isLastInStockFn = (sku: Sku) => {
  if (!sku.stock_items?.length) return false

  const exactlyOneLastInStock =
    sku.stock_items.filter(isStockItemLastInStock).length === 1

  const allTheOthersOutOfStock = sku.stock_items
    .filter(stockItem => !isStockItemLastInStock(stockItem))
    .every(stockItem => !isStockItemInStock(stockItem))

  return exactlyOneLastInStock && allTheOthersOutOfStock
}

/**
 * Checks if a stock item is available for purchase.
 *
 * A stock item is considered in stock if its available quantity
 * is greater than its reserved stock.
 *
 * @param {StockItem} stockItem - The stock item to check.
 * @returns {boolean} True if the stock item is available, false otherwise.
 *
 * @private
 */
const isStockItemInStock = (stockItem: StockItem) =>
  stockItem.quantity > (stockItem.reserved_stock?.quantity ?? 0)

/**
 * Determines if a stock item has exactly one item left.
 *
 * Checks if the available quantity is exactly one after
 * subtracting reserved stock.
 *
 * @param {StockItem} stockItem - The stock item to check.
 * @returns {boolean} True if the stock item has exactly one item left, false otherwise.
 *
 * @private
 */
const isStockItemLastInStock = (stockItem: StockItem) =>
  stockItem.quantity - (stockItem.reserved_stock?.quantity ?? 0) === 1

/**
 * Retrieves price and stock information for a SKU.
 *
 * Calculates the total available quantity across all stock items
 * and retrieves the first price for the SKU.
 *
 * @param {Sku | undefined} sku - The SKU to get information for.
 * @returns {Object} An object containing price and available quantity.
 * @property {Object | null} price - The first price for the SKU, or null if no price exists.
 * @property {number} availableQty - The total available quantity across all stock items.
 *
 * @example
 * // Returns price and available quantity for a SKU
 * getSkuPriceAndStockInfo(mySku)
 *
 * @example
 * // Returns default values for undefined SKU
 * getSkuPriceAndStockInfo(undefined)
 */
export const getSkuPriceAndStockInfo = (sku: Sku | undefined) => {
  if (!sku)
    return {
      price: null,
      availableQty: 0,
    }
  const { prices, stock_items } = sku
  const price = prices?.[0]
  const availableQty =
    stock_items?.reduce(
      (availableQty, stockItem) =>
        availableQty +
        stockItem.quantity -
        (stockItem.reserved_stock?.quantity ?? 0),
      0
    ) ?? 0

  return {
    price,
    availableQty,
  }
}
